'use client'

import { useAuthorization } from '@entities/authentication'
import {
  homeBannersEntityQueries,
  isHomeBannerVisibleForCountry,
  type HomeBanner,
  type HomeBannerPlacement,
  type HomeBannersResponse,
} from '@entities/home-banners'
import { Carousel, type CarouselApi, CarouselContent, CarouselIndicator, CarouselItem } from '@shared/ui'
import { navigateToUrl, normalizeCountryCode } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState, type ComponentProps, type CSSProperties } from 'react'

import { HomeBannerSkeleton } from './home-banner-skeleton'

export const HOME_BANNER_IMAGE_PROPS: Partial<ComponentProps<typeof Image>> = {
  width: 1005,
  height: 252,
  className: 'h-auto w-full object-cover',
  sizes: '100vw',
}

export const HOME_BANNER_CONTAINER_CLASS_NAME = 'ml-0 aspect-[335/84] overflow-hidden rounded-lg'

type CarouselState = {
  current: number
  count: number
}

type HomeBannerImageProps = {
  banner: HomeBanner
  onClick: (banner: HomeBanner) => void
  onImageLoad: (id: string, ratio: number) => void
  isFirst: boolean
}

export const getBannerIndicatorState = ({
  bannerCount,
  carouselState,
}: {
  bannerCount: number
  carouselState: CarouselState
}): CarouselState => {
  if (bannerCount <= 0) {
    return { count: 0, current: 0 }
  }

  return {
    count: bannerCount,
    current: Math.min(Math.max(carouselState.current || 1, 1), bannerCount),
  }
}

const HomeBannerImage = ({ banner, onClick, onImageLoad, isFirst }: HomeBannerImageProps) => {
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const image = imageRef.current
    if (!image) {
      return
    }

    const updateRatio = () => {
      if (image.naturalWidth && image.naturalHeight) {
        onImageLoad(banner.id, image.naturalWidth / image.naturalHeight)
      }
    }

    // Next Image deduplicates onLoad by src; picture media changes only currentSrc.
    image.addEventListener('load', updateRatio)
    if (image.complete) {
      updateRatio()
    }
    return () => image.removeEventListener('load', updateRatio)
  }, [banner.id, banner.imageUrl, banner.pcImageUrl, onImageLoad])

  const image = banner.pcImageUrl ? (
    <picture>
      <source media="(min-width: 1024px)" srcSet={banner.pcImageUrl} />
      <Image
        ref={imageRef}
        src={banner.imageUrl}
        alt={banner.identifier}
        unoptimized
        {...HOME_BANNER_IMAGE_PROPS}
        loading={isFirst ? 'eager' : 'lazy'}
        fetchPriority={isFirst ? 'high' : 'auto'}
        className="aspect-[335/84] w-full object-cover min-[1024px]:aspect-auto min-[1024px]:h-auto"
      />
    </picture>
  ) : (
    <Image
      ref={imageRef}
      src={banner.imageUrl}
      alt={banner.identifier}
      unoptimized
      {...HOME_BANNER_IMAGE_PROPS}
      loading={isFirst ? 'eager' : 'lazy'}
      fetchPriority={isFirst ? 'high' : 'auto'}
    />
  )

  if (banner.landingType === 'NONE') {
    return <div className="block h-full w-full">{image}</div>
  }

  return (
    <button
      type="button"
      aria-label={banner.identifier}
      className="block h-full w-full appearance-none border-0 bg-transparent p-0 text-left"
      onClick={() => onClick(banner)}
    >
      {image}
    </button>
  )
}

type ApiHomeBannerCarouselProps = {
  countryCode?: string | null
  placement?: HomeBannerPlacement
  className?: string
  initialBanners?: HomeBannersResponse | null
}

export const ApiHomeBannerCarousel = ({
  countryCode,
  placement = 'HOME',
  className,
  initialBanners,
}: ApiHomeBannerCarouselProps) => {
  const { bearerToken } = useAuthorization()
  const router = useRouter()

  const [imageRatios, setImageRatios] = useState<Record<string, number>>({})
  const onImageLoad = useCallback((id: string, ratio: number) => {
    setImageRatios((previous) => (previous[id] === ratio ? previous : { ...previous, [id]: ratio }))
  }, [])

  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [carouselState, setCarouselState] = useState<CarouselState>({
    current: 0,
    count: 0,
  })

  const bannerQuery = homeBannersEntityQueries.getHomeBanners({
    bearerToken,
    countryCode,
    placement,
  })
  const { data, isError, isPending } = useQuery({
    ...bannerQuery,
    ...(initialBanners !== undefined
      ? {
          initialData: initialBanners ?? undefined,
          staleTime: 60_000,
          enabled: initialBanners !== null && bannerQuery.enabled !== false,
        }
      : {}),
  })

  const handleBannerClick = useCallback(
    (banner: HomeBanner) => {
      if (banner.landingType === 'NONE') {
        return
      }
      navigateToUrl(banner.landingUrl, router)
    },
    [router],
  )

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    const updateCarouselState = () => {
      setCarouselState(() => ({
        count: carouselApi.scrollSnapList().length,
        current: carouselApi.selectedScrollSnap() + 1,
      }))
    }

    updateCarouselState()
    carouselApi.on('select', updateCarouselState)

    return () => {
      carouselApi.off('select', updateCarouselState)
    }
  }, [carouselApi])

  if ((initialBanners === null && !data) || (!bearerToken && placement === 'HOME')) {
    return null
  }

  if (isPending) {
    return <HomeBannerSkeleton className={className} />
  }

  if (isError && !data) {
    return null
  }

  const banners = (data?.banners ?? []).filter((banner) =>
    isHomeBannerVisibleForCountry({
      banner,
      countryCode: normalizeCountryCode(countryCode),
    }),
  )

  const indicatorState = getBannerIndicatorState({
    bannerCount: banners.length,
    carouselState,
  })
  const containerClassName = [
    HOME_BANNER_CONTAINER_CLASS_NAME,
    banners.some((banner) => banner.pcImageUrl)
      ? banners.length > 1
        ? 'min-[1024px]:aspect-[var(--banner-aspect-ratio)]'
        : 'min-[1024px]:aspect-auto'
      : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  if (banners.length === 0) {
    return null
  }

  if (banners.length === 1) {
    return (
      <div className={containerClassName}>
        <HomeBannerImage banner={banners[0]} onClick={handleBannerClick} onImageLoad={onImageLoad} isFirst />
      </div>
    )
  }

  return (
    <Carousel
      className={`relative ${containerClassName}`}
      style={
        { '--banner-aspect-ratio': imageRatios[banners[indicatorState.current - 1].id] ?? 335 / 84 } as CSSProperties
      }
      setApi={setCarouselApi}
      opts={{ loop: true }}
      plugins={[Autoplay({ delay: 5000 })]}
      data-testid="list-banners"
    >
      <CarouselContent className="ml-0 items-start">
        {banners.map((banner, index) => (
          <CarouselItem key={banner.id} className="shrink-0 w-full pl-0">
            <HomeBannerImage
              banner={banner}
              onClick={handleBannerClick}
              onImageLoad={onImageLoad}
              isFirst={index === 0}
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      <CarouselIndicator
        className="absolute bottom-2 right-2 z-10 inline-flex items-center justify-center"
        color="white"
        size="small"
        count={indicatorState.count}
        current={indicatorState.current}
      />
    </Carousel>
  )
}
